// src/components/tokenPage/overview/FundingTrendCard.tsx

import React, { useMemo, useState } from "react";
import { TimeRangePicker } from "@/components/common";
import type { TimeRange } from "@/types/charts";
import { DataCard } from "@/components/ui";
import { TimeSeriesChart } from "@/components/charts/base";
import { useTokenDerivatives } from "@/contexts/token";
import { formatDateLabel } from "@/utils/charts/dataUtils";

interface Props { ticker: string }

const RANGE_TO_DAYS: Record<TimeRange, number> = { '7d': 7, '30d': 30, '90d': 90, '180d': 180, '365d': 365, 'All': 9999 };

const FundingTrendCard: React.FC<Props> = () => {
  const [range, setRange] = useState<TimeRange>('90d');
  const { fundingOiWeighted, isLoading } = useTokenDerivatives();

  const { dates, values } = useMemo(() => {
    const series = fundingOiWeighted.series;
    if (series.length === 0) {
      return { dates: [], values: [] };
    }
    
    const maxPoints = RANGE_TO_DAYS[range];
    const start = Math.max(0, series.length - maxPoints);
    const sliced = series.slice(start);
    
    return {
      dates: sliced.map(p => new Date(p.timestamp).toISOString().slice(0, 10)), // ISO format for chart axis
      values: sliced.map(p => p.rate),
    };
  }, [fundingOiWeighted.series, range]);

  // Hide if no data available (after loading)
  if (!isLoading && (!values.length || values.every(v => v == null))) {
    return null;
  }

  return (
    <DataCard
      title="Funding trend"
      subtitle="OI-weighted funding rate"
      icon="trending-up"
      iconColor="text-violet-400"
      isLoading={isLoading}
      loadingVariant="chart"
      actions={
        <TimeRangePicker options={['7d','30d','90d','180d','365d'] as const} value={range} onChange={setRange} />
      }
    >
      <TimeSeriesChart
        dates={dates}
        series={[{ name: 'Funding Rate', values, color: '#8b5cf6', areaStyle: true }]}
        height={400}
        yAxisFormatter={(v) => `${v.toFixed(3)}%`}
        tooltipFormatter={(params) => {
          const items = params as Array<{ axisValue: string; value: number }>;
          if (!items || items.length === 0) return '';
          const item = items[0];
          const dateStr = item?.axisValue || '';
          const formattedDate = formatDateLabel(dateStr, 'medium');
          const val = Number(item?.value ?? 0);
          const sign = val >= 0 ? '+' : '';
          const color = val >= 0 ? '#10b981' : '#ef4444';
          return `<div style="font-size:11px;font-weight:500;margin-bottom:4px;color:#9ca3af;">${formattedDate}</div><div style="font-weight:600;color:${color};">${sign}${val.toFixed(4)}%</div>`;
        }}
        showLegend={false}
      />
    </DataCard>
  );
};

export default FundingTrendCard;
